import { ReactNode } from 'react'
import cx from 'classnames'
import { CurrencyDisplay } from '@pogokid/ui'
import { GlassSurface } from '../GlassSurface'
import { Headline } from '../Headline/Headline'
import { Text } from '../Text'
import styles from './GlassReceipt.module.css'

export interface GlassReceiptLine {
  /** Left-hand label for the row, e.g. "Invoice" */
  label: ReactNode
  /**
   * Right-hand value for the row. Pass a plain string for default body
   * styling, or a styled `<Text>` (e.g. `variant="invoice-number"` /
   * `variant="date"`) to control the semantic colour.
   */
  value: ReactNode
}

export interface GlassReceiptProps {
  /** Small label shown above the amount, e.g. "Amount paid" */
  eyebrow?: ReactNode
  /**
   * The headline amount, expressed in the currency's minor units
   * (e.g. `6000` renders as `£60.00`).
   */
  amount: number
  /**
   * ISO 4217 currency code used to format the amount.
   *
   * @default 'GBP'
   */
  currency?: string
  /**
   * Status badge shown beneath the amount. Pass a rendered chip,
   * e.g. `<StatusChip variant="success" label="Paid" />` or an
   * `<InvoiceStatusChip />`.
   */
  status?: ReactNode
  /** Optional branding/icon shown in the top-right of the header. */
  icon?: ReactNode
  /**
   * Receipt rows. Every line is optional — omit any that don't apply to a
   * given receipt. An empty/omitted list hides the perforated lower section.
   */
  lines?: GlassReceiptLine[]
  /** Additional CSS class */
  className?: string
}

export function GlassReceipt({
  eyebrow,
  amount,
  currency = 'GBP',
  status,
  icon,
  lines,
  className,
}: GlassReceiptProps) {
  const hasLines = !!lines && lines.length > 0
  return (
    <GlassSurface
      variant="card"
      className={cx(styles.GlassReceipt, className)}
    >
      <div className={styles.header}>
        <div className={styles.headerMain}>
          {eyebrow ? (
            <Text variant="eyebrow" component="div">
              {eyebrow}
            </Text>
          ) : null}
          <Headline size="lg" component="div">
            <CurrencyDisplay
              currency={currency}
              value={amount}
            />
          </Headline>
          {status}
        </div>
        {icon ? (
          <div className={styles.icon} aria-hidden="true">
            {icon}
          </div>
        ) : null}
      </div>

      {hasLines ? (
        <>
          <div
            className={styles.perforation}
            aria-hidden="true"
          />
          <dl className={styles.lines}>
            {lines!.map((line, index) => (
              <div className={styles.line} key={index}>
                <dt className={styles.lineLabel}>
                  <Text variant="body" component="span">
                    {line.label}
                  </Text>
                </dt>
                <dd className={styles.lineValue}>
                  {typeof line.value === 'string' ? (
                    <Text variant="body" component="span">
                      {line.value}
                    </Text>
                  ) : (
                    line.value
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </>
      ) : null}
    </GlassSurface>
  )
}
